import { chakra, type SystemStyleObject } from '@chakra-ui/react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { controlSize, type ControlSize } from './_shared';
import type { SelectMenuOption } from './SelectMenu';

/**
 * A controlled async typeahead (text input + popup `role="listbox"`). Unlike `SelectMenu`
 * (a fixed option set), the caller OWNS the input text and the (usually server-fetched,
 * debounced) option list: type → the caller queries → passes fresh `options` back. Picking an
 * option calls `onSelect`; the caller decides what the input then shows. Built on the same
 * token-driven field + listbox styling as `TextInput` / `SelectMenu` so it sits natively in a
 * form. Fully keyboard-operable (↑/↓/Home/End, Enter, Escape) and screen-reader labelled.
 */

const fieldBase: SystemStyleObject = {
  width: 'full',
  minWidth: 0,
  fontFamily: 'body',
  color: 'text.primary',
  bg: 'surface.default',
  borderWidth: '1px',
  borderColor: 'border.strong',
  borderRadius: 'md',
  transitionProperty: 'border-color, box-shadow, background-color',
  transitionDuration: 'fast',
  transitionTimingFunction: 'standard',
  outline: 'none',
  _placeholder: { color: 'text.muted' },
  _hover: { borderColor: 'border.strong' },
  _focusVisible: { borderColor: 'action.primary', boxShadow: 'focus' },
  _disabled: { opacity: 0.55, cursor: 'not-allowed', bg: 'surface.subtle' },
};

const Anchor = chakra('div', { base: { position: 'relative', display: 'block', minWidth: 0 } });
const Field = chakra('input', { base: { ...fieldBase, paddingRight: '9' } });

const ClearButton = chakra('button', {
  base: {
    position: 'absolute',
    top: '50%',
    right: '2',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: '6',
    borderRadius: 'sm',
    color: 'text.muted',
    cursor: 'pointer',
    _hover: { color: 'text.primary', bg: 'action.secondary' },
  },
});

const Listbox = chakra('ul', {
  base: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 'dropdown',
    listStyleType: 'none',
    margin: 0,
    padding: '1',
    maxHeight: '18rem',
    overflowY: 'auto',
    bg: 'surface.raised',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'lg',
    boxShadow: 'lg',
  },
});

const Option = chakra('li', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5',
    px: '2.5',
    py: '2',
    borderRadius: 'md',
    cursor: 'pointer',
    color: 'text.primary',
    fontSize: 'md',
    lineHeight: 'snug',
    _hover: { bg: 'action.secondary' },
    '&[data-active="true"]': { bg: 'action.secondary' },
    '&[aria-disabled="true"]': { opacity: 0.5, cursor: 'not-allowed', _hover: { bg: 'transparent' } },
  },
});

const Status = chakra('li', {
  base: { px: '2.5', py: '2', color: 'text.muted', fontSize: 'sm', listStyleType: 'none' },
});

export interface ComboboxProps {
  /** The text currently in the input (controlled by the caller). */
  inputValue: string;
  /** Called on every keystroke — the caller debounces this into its query. */
  onInputChange: (value: string) => void;
  /** The current option list (usually the caller's debounced query result). */
  options: SelectMenuOption[];
  /** Called when an option is chosen (click / Enter). */
  onSelect: (option: SelectMenuOption) => void;
  /** Accessible label for the input. */
  label: string;
  placeholder?: string;
  /** Show a loading row in the popup while the caller's query is in flight. */
  loading?: boolean;
  /** Copy for the "no matches" row (shown when not loading and the query yielded nothing). */
  emptyText?: string;
  /** Minimum input length before the popup opens (default 0). */
  minChars?: number;
  /** When set, a clear (✕) button appears once the input is non-empty. */
  onClear?: () => void;
  size?: ControlSize;
  disabled?: boolean;
  'data-testid'?: string;
}

export function Combobox({
  inputValue,
  onInputChange,
  options,
  onSelect,
  label,
  placeholder,
  loading = false,
  emptyText = 'No matches.',
  minChars = 0,
  onClear,
  size = 'md',
  disabled,
  'data-testid': testId,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  // The popup is meaningful only once the query is long enough to have queried on.
  const meetsMin = inputValue.trim().length >= minChars;
  const showPopup = open && meetsMin && (loading || options.length > 0 || inputValue.trim().length > 0);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    if (!showPopup) return;
    const onDocDown = (e: MouseEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [showPopup, close]);

  // Keep the active row in range as the option list changes under the caller's query.
  useEffect(() => {
    setActiveIndex((cur) => (cur >= options.length ? -1 : cur));
  }, [options.length]);

  const commit = useCallback(
    (idx: number) => {
      const opt = options[idx];
      if (!opt || opt.disabled) return;
      onSelect(opt);
      close();
    },
    [options, onSelect, close],
  );

  const move = (delta: number) => {
    if (options.length === 0) return;
    setActiveIndex((cur) => {
      const n = options.length;
      let next = cur < 0 ? (delta > 0 ? 0 : n - 1) : cur;
      for (let i = 0; i < n; i++) {
        next = (next + (cur < 0 ? 0 : delta) + n) % n;
        if (!options[next]?.disabled) break;
      }
      return next;
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Home':
        if (showPopup) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (showPopup) {
          e.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
      case 'Enter':
        if (showPopup && activeIndex >= 0) {
          e.preventDefault();
          commit(activeIndex);
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          close();
        }
        break;
    }
  };

  const activeId = activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined;

  return (
    <Anchor ref={anchorRef}>
      <Field
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showPopup}
        aria-controls={showPopup ? listboxId : undefined}
        aria-activedescendant={activeId}
        aria-label={label}
        autoComplete="off"
        placeholder={placeholder}
        value={inputValue}
        disabled={disabled}
        css={controlSize[size]}
        data-testid={testId}
        onChange={(e) => {
          onInputChange(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {onClear && inputValue.length > 0 && !disabled && (
        <ClearButton
          type="button"
          aria-label="Clear"
          data-testid={testId ? `${testId}-clear` : undefined}
          onClick={() => {
            onClear();
            close();
            inputRef.current?.focus();
          }}
        >
          <X size={14} strokeWidth={2} aria-hidden />
        </ClearButton>
      )}
      {showPopup && (
        <Listbox id={listboxId} role="listbox" aria-label={label}>
          {loading ? (
            <Status aria-disabled>Searching…</Status>
          ) : options.length === 0 ? (
            <Status aria-disabled>{emptyText}</Status>
          ) : (
            options.map((opt, i) => (
              <Option
                key={opt.value}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                aria-disabled={opt.disabled || undefined}
                data-active={i === activeIndex || undefined}
                data-testid={opt['data-testid']}
                // `onMouseDown` (not `onClick`) so the pick fires before the input's blur closes the popup.
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(i);
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <chakra.span overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {opt.label}
                </chakra.span>
                {opt.hint && (
                  <chakra.span fontSize="xs" color="text.muted" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {opt.hint}
                  </chakra.span>
                )}
              </Option>
            ))
          )}
        </Listbox>
      )}
    </Anchor>
  );
}
